import { faClock } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from 'atoms/icons';
import { Gift } from 'atoms/icons/Gift';
import { Horizontal } from 'atoms/layout/flex';
import { LabelS } from 'atoms/typography';
import { TimeLeft } from 'contexts/ticker/TimeLeft';
import { useTimeLeft } from 'hooks/useTimeLeft';
import { shortTimeLeftMessages } from 'lib/glossary';

const Dot = styled.div`
  background-color: var(--c-white);
  border-radius: 50%;
  width: var(--half-unit);
  height: var(--half-unit);
`;

const Tag = styled(Horizontal).attrs({ gap: 0.5, center: true })`
  border-radius: 3px 3px 14px 14px;
  padding: 0 var(--half-unit);
  width: 100%;
  white-space: nowrap;
`;

const LiveTag = styled(Tag)`
  background-color: var(--c-red-400);
`;

const RewardsTag = styled(Tag)`
  background-color: var(--c-green-400);
  color: var(--c-black);
`;

const LiveLabel = styled(LabelS)`
  text-transform: uppercase;
`;

const NextUpcomingTagWrapper = styled(Tag)`
  background-color: var(--c-yellow-400);
  & > svg {
    font-size: 0.675rem;
  }
`;

const NextUpcomingTag = ({ startDate }: { startDate: Date }) => {
  const { message } = useTimeLeft(startDate, {
    customMessages: shortTimeLeftMessages,
  });
  return (
    <NextUpcomingTagWrapper>
      <FontAwesomeIcon icon={faClock} size="xs" color="var(--c-black)" />
      <LabelS as="p" bold color="var(--c-black)">
        {message}
      </LabelS>
    </NextUpcomingTagWrapper>
  );
};

type Props = {
  isLive: boolean;
  isUpcoming: Nullable<boolean>;
  startDate: Nullable<Date>;
  expectedRewardsDistributionDate: Nullable<Date>;
  waitingForRewards: Nullable<boolean>;
};

export const FixtureItemTag = ({
  isLive,
  isUpcoming,
  startDate,
  expectedRewardsDistributionDate,
  waitingForRewards,
}: Props) => {
  if (isLive) {
    return (
      <LiveTag>
        <Dot />
        <LiveLabel as="p" bold color="var(--c-white)">
          <FormattedMessage id="FixturePickerItem.live" defaultMessage="Live" />
        </LiveLabel>
      </LiveTag>
    );
  }
  if (isUpcoming && startDate) {
    return <NextUpcomingTag startDate={startDate} />;
  }

  if (waitingForRewards && expectedRewardsDistributionDate) {
    return (
      <RewardsTag>
        <Gift size={1.5} />
        <LabelS as="p" bold>
          <TimeLeft
            time={expectedRewardsDistributionDate}
            options={{
              customMessages: shortTimeLeftMessages,
            }}
          />
        </LabelS>
      </RewardsTag>
    );
  }
  return null;
};
